import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  View,
  FlatList,
  RefreshControl,
  StyleSheet,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { Text } from '@/components/common/text';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAppSelector, useAppDispatch } from '@/store';
import {
  useGetTagsQuery,
  useGetThreadsQuery,
  useToggleSaveThreadMutation,
  useMarkNewsfeedSeenMutation,
} from '@/store/api/corkBoard/corkBoardApi';
import { clearDeletedThreads, clearFeedReplace, trackDeletedThread } from '@/store/slices/residents/corkBoardUISlice';
import { useCorkBoardMutation } from '@/hooks/residents/corkBoard/useCorkBoardMutation';
import { useThreadActions } from '@/hooks/residents/corkBoard/useThreadActions';
import { useToast } from '@/contexts/admin/ToastContext';
import { useAccumulatedPaging } from '@/hooks/common/useAccumulatedPaging';
import { useCorkBoardFilters } from '@/hooks/residents/corkBoard/useCorkBoardFilters';

import {
  ThreadCardSkeleton,
  CorkBoardEmptyState,
} from '@/components/residents/corkBoard';
import {
  GlassCard,
  PageHeader,
  NewsfeedThreadCard,
  NewsfeedTagFilter,
  NewsfeedSearchBar,
  NewsfeedActionButtons,
  NewsfeedSortRow,
} from '@/components/residents/redesign';
import { ScreenBackLink } from '@/components/residents/mobile/ScreenBackLink';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import type { CorkBoardThreadSummary } from '@/types/residents/corkBoard';

type CommunityBoardNavigationProp = StackNavigationProp<RootStackParamList>;

const HP = REDESIGN_SPACING.horizontalPadding;

export const CommunityBoardScreen: React.FC = () => {
  const { colors } = useThemeColors();
  const navigation = useNavigation<CommunityBoardNavigationProp>();
  const dispatch = useAppDispatch();
  const { layoutMode } = useResponsive();
  const user = useAppSelector((state) => state.auth.user);
  const propertyId = user?.propertyId;
  const propertyName = user?.property?.marketingName || user?.property?.legalName || '';
  const deletedThreadIds = useAppSelector((state) => state.corkBoardUI.deletedThreadIds);
  const feedNeedsReplace = useAppSelector((state) => state.corkBoardUI.feedNeedsReplace);

  const insets = useSafeAreaInsets();
  const isMobile = layoutMode === 'mobile';
  const isTablet = layoutMode === 'tablet';
  const isDesktop = !isMobile && !isTablet;

  const filters = useCorkBoardFilters({ propertyId, kind: 'thread' });

  // Stamp the newsfeed visit so the home Quick Actions "N new" badge clears.
  // Fire-and-forget: the mutation optimistically zeroes the cached count.
  const [markNewsfeedSeen] = useMarkNewsfeedSeenMutation();
  useEffect(() => {
    if (propertyId) {
      markNewsfeedSeen({ propertyId });
    }
  }, [propertyId, markNewsfeedSeen]);

  const {
    data: rawTags = [],
    isLoading: isLoadingTags,
    refetch: refetchTags,
  } = useGetTagsQuery(
    { propertyId: propertyId ?? 0 },
    { skip: !propertyId },
  );

  const tags = useMemo(
    () => [...rawTags].sort((a, b) => (a.slug === 'announcements' ? -1 : b.slug === 'announcements' ? 1 : 0)),
    [rawTags],
  );

  const {
    currentData: threadsData,
    isLoading: isLoadingThreads,
    isFetching: isFetchingThreads,
    refetch: refetchThreads,
    isError: isThreadsError,
  } = useGetThreadsQuery(
    filters.queryParams,
    { skip: filters.shouldSkipQuery },
  );

  const threads = threadsData?.threads ?? [];
  const announcements = useMemo(() => threadsData?.announcements ?? [], [threadsData?.announcements]);
  const pagination = threadsData?.pagination;
  const totalCount = pagination?.totalCount ?? 0;
  const hasMorePages = pagination ? pagination.currentPage < pagination.totalPages - 1 : false;

  const { accumulatedRows, clearIfPendingReplaceAndNoRows, markForReplace } = useAccumulatedPaging({
    rows: threads,
    accumulationKey: filters.accumulationKey,
  });

  React.useEffect(() => {
    clearIfPendingReplaceAndNoRows(isLoadingThreads);
  }, [isLoadingThreads, clearIfPendingReplaceAndNoRows]);

  useEffect(() => {
    if (feedNeedsReplace) {
      markForReplace();
      dispatch(clearFeedReplace());
      if (filters.page !== 0) {
        filters.resetPage();
      }
    }
  }, [feedNeedsReplace, markForReplace, dispatch, filters]);

  const visibleRows = useMemo(
    () => deletedThreadIds.length > 0
      ? accumulatedRows.filter((t) => !deletedThreadIds.includes(String(t.id)))
      : accumulatedRows,
    [accumulatedRows, deletedThreadIds],
  );

  const isInitialLoad = isLoadingThreads && visibleRows.length === 0;
  const isLoadingMore = isFetchingThreads && filters.page > 0;

  const [isManualRefreshing, setIsManualRefreshing] = useState(false);

  useEffect(() => {
    if (!isFetchingThreads) {
      setIsManualRefreshing(false);
    }
  }, [isFetchingThreads]);

  const handleThreadPress = useCallback((thread: CorkBoardThreadSummary) => {
    navigation.navigate('ThreadDetail', { threadId: thread.id });
  }, [navigation]);

  const handleTagPressInCard = useCallback((tagId: number) => {
    filters.toggleTag(tagId);
  }, [filters]);

  const handleCreatePost = useCallback(() => {
    navigation.navigate('CreateThread');
  }, [navigation]);

  const handleMyPosts = useCallback(() => {
    navigation.navigate('MyThreads');
  }, [navigation]);

  const handleSavedPosts = useCallback(() => {
    navigation.navigate('SavedThreads');
  }, [navigation]);

  const { showSuccess, showError } = useToast();
  const [toggleSaveTrigger] = useToggleSaveThreadMutation();

  const { execute: executeToggleSave } = useCorkBoardMutation<string, any>(
    toggleSaveTrigger, { errorFallback: 'Failed to update bookmark.' },
  );

  const {
    handleEditThread,
    openDeleteConfirm,
    openReportModal,
    threadActionModals,
  } = useThreadActions({
    onDeleteSuccess: (thread) => dispatch(trackDeletedThread(thread.id)),
  });

  const handleSaveThread = useCallback(async (thread: CorkBoardThreadSummary) => {
    const result = await executeToggleSave(thread.id, { silent: true });
    if (result.success) {
      const saved = result.data?.saved ?? !thread.isSaved;
      showSuccess(saved ? 'Thread saved' : 'Thread unsaved');
    } else {
      showError('Failed to save thread');
    }
  }, [executeToggleSave, showSuccess, showError]);

  const handleRefresh = useCallback(() => {
    setIsManualRefreshing(true);
    dispatch(clearDeletedThreads());
    markForReplace();
    refetchTags();
    if (filters.page === 0) {
      refetchThreads();
    } else {
      filters.resetPage();
    }
  }, [filters, refetchThreads, refetchTags, markForReplace, dispatch]);

  const handleEndReached = useCallback(() => {
    if (!isFetchingThreads && hasMorePages) {
      filters.loadNextPage();
    }
  }, [isFetchingThreads, hasMorePages, filters]);

  const renderThread = useCallback(({ item }: { item: CorkBoardThreadSummary }) => (
    <NewsfeedThreadCard
      thread={item}
      onPress={handleThreadPress}
      onTagPress={handleTagPressInCard}
      onSave={handleSaveThread}
      onEdit={handleEditThread}
      onDelete={openDeleteConfirm}
      onReport={openReportModal}
    />
  ), [handleThreadPress, handleTagPressInCard, handleSaveThread, handleEditThread, openDeleteConfirm, openReportModal]);

  const keyExtractor = useCallback((item: CorkBoardThreadSummary) => item.id, []);

  const ListHeaderComponent = useMemo(() => (
    <View style={styles.headerSection}>
      {!isDesktop && <ScreenBackLink style={styles.backLink} />}
      <PageHeader title="News Feed" subtitle={propertyName} />

      {!isDesktop && <NewsfeedActionButtons onMyPosts={handleMyPosts} onSaved={handleSavedPosts} />}

      <NewsfeedSearchBar
        value={filters.searchInput}
        onChangeText={filters.setSearch}
        onClear={filters.clearSearch}
      />

      {!isDesktop && !isLoadingTags && tags.length > 0 && (
        <NewsfeedTagFilter
          tags={tags}
          selectedTagIds={filters.selectedTagIds}
          onToggleTag={filters.toggleTag}
          onClearFilters={filters.clearTags}
        />
      )}

      <NewsfeedSortRow
        postCount={totalCount}
        sortBy={filters.sortBy}
        onSortChange={filters.setSortBy}
      />

      {announcements.length > 0 && (
        <View style={styles.announcementsSection}>
          {announcements.map((thread) => (
            <NewsfeedThreadCard
              key={thread.id}
              thread={thread}
              onPress={handleThreadPress}
              onTagPress={handleTagPressInCard}
              onSave={handleSaveThread}
              onEdit={handleEditThread}
              onDelete={openDeleteConfirm}
              onReport={openReportModal}
            />
          ))}
        </View>
      )}
    </View>
  ), [
    propertyName,
    isDesktop, handleMyPosts, handleSavedPosts,
    filters.searchInput, filters.setSearch, filters.clearSearch,
    isLoadingTags, tags, filters.selectedTagIds, filters.toggleTag, filters.clearTags,
    totalCount, filters.sortBy, filters.setSortBy,
    announcements, handleThreadPress, handleTagPressInCard, handleSaveThread, handleEditThread, openDeleteConfirm, openReportModal,
  ]);

  const ListEmptyComponent = useMemo(() => {
    if (isInitialLoad || (isFetchingThreads && visibleRows.length === 0)) return <ThreadCardSkeleton />;
    if (isThreadsError) return <CorkBoardEmptyState variant="error" onRetry={handleRefresh} />;
    if (filters.hasActiveFilters && announcements.length === 0) return <CorkBoardEmptyState variant="noResults" />;
    if (announcements.length > 0) return null;
    return <CorkBoardEmptyState variant="noThreads" onCreatePost={handleCreatePost} />;
  }, [isInitialLoad, isFetchingThreads, visibleRows.length, isThreadsError, filters.hasActiveFilters, announcements.length, handleRefresh, handleCreatePost]);

  const ListFooterComponent = useMemo(() => {
    if (!isLoadingMore) return null;
    return (
      <View style={styles.footerLoader}>
        <ActivityIndicator size="small" color={PRIMITIVE_COLORS.resioBlue} />
      </View>
    );
  }, [isLoadingMore]);

  const renderFeed = () => (
    <FlatList
      data={visibleRows}
      renderItem={renderThread}
      keyExtractor={keyExtractor}
      ListHeaderComponent={ListHeaderComponent}
      ListEmptyComponent={ListEmptyComponent}
      ListFooterComponent={ListFooterComponent}
      contentContainerStyle={[
        styles.feedContentContainer,
        // On desktop the outer desktopContent already provides horizontal
        // padding; drop the list's own so feed content lines up with the
        // marketplace content edge instead of being inset twice.
        isDesktop && styles.feedContentContainerDesktop,
        { paddingBottom: 80 + insets.bottom },
      ]}
      ItemSeparatorComponent={ItemSeparator}
      onEndReached={handleEndReached}
      onEndReachedThreshold={0.4}
      showsVerticalScrollIndicator={false}
      removeClippedSubviews={Platform.OS !== 'web'}
      maxToRenderPerBatch={10}
      windowSize={5}
      initialNumToRender={10}
      updateCellsBatchingPeriod={50}
      refreshControl={
        <RefreshControl
          refreshing={isManualRefreshing}
          onRefresh={handleRefresh}
          tintColor={colors.text.primary}
        />
      }
    />
  );

  const fab = (
    <Hoverable
      hover="button"
      style={[styles.fab, { bottom: 20 + insets.bottom }]}
      onPress={handleCreatePost}
      accessibilityRole="button"
      accessibilityLabel="Create new post"
    >
      <RESIDENTS_SVG_COMPONENTS.CIRCLE_PLUS width={22} height={22} color="#FFFFFF" />
    </Hoverable>
  );

  if (isMobile || isTablet) {
    return (
      <>
        <View style={styles.feedContainer}>
          {renderFeed()}
          {fab}
        </View>
        {threadActionModals}
      </>
    );
  }

  return (
    <View style={styles.desktopContainer}>
      <View style={styles.desktopContent}>
        <View style={styles.desktopFeedColumn}>
          {renderFeed()}
        </View>
        <View style={styles.desktopSidePanel}>
          <GlassCard borderRadius={16} padding={0}>
            <View style={styles.sidePanelHeader}>
              <Text style={[styles.sidePanelTitle, { color: colors.text.primary }]}>Community</Text>
              <Text style={[styles.sidePanelProperty, { color: colors.text.dim }]}>{propertyName}</Text>
              <View style={styles.sidePanelStats}>
                <Text style={[styles.sidePanelStatValue, { color: colors.text.primary }]}>{totalCount}</Text>
                <Text style={[styles.sidePanelStatLabel, { color: colors.text.dimmer }]}>posts</Text>
              </View>
            </View>
            <View style={[styles.sidePanelDivider, { backgroundColor: colors.separator }]} />
            <View style={styles.sidePanelActions}>
              <Hoverable
                hover="button"
                style={styles.sidePanelButton}
                onPress={handleCreatePost}
                accessibilityRole="button"
                accessibilityLabel="Create new post"
              >
                <RESIDENTS_SVG_COMPONENTS.CIRCLE_PLUS width={16} height={16} color="#FFFFFF" />
                <Text style={styles.sidePanelButtonText}>Create Post</Text>
              </Hoverable>
            </View>
          </GlassCard>

          <GlassCard borderRadius={16} padding={0}>
            <Hoverable
              hover="row"
              style={styles.sideLink}
              onPress={handleMyPosts}
              accessibilityRole="button"
              accessibilityLabel="My Posts"
            >
              <RESIDENTS_SVG_COMPONENTS.USER width={16} height={16} color={colors.text.muted} />
              <Text style={[styles.sideLinkText, { color: colors.text.secondary }]}>My Posts</Text>
            </Hoverable>
            <View style={[styles.sideLinkDivider, { backgroundColor: colors.separator }]} />
            <Hoverable
              hover="row"
              style={styles.sideLink}
              onPress={handleSavedPosts}
              accessibilityRole="button"
              accessibilityLabel="Saved"
            >
              <RESIDENTS_SVG_COMPONENTS.STAR width={16} height={16} color={colors.text.muted} />
              <Text style={[styles.sideLinkText, { color: colors.text.secondary }]}>Saved</Text>
            </Hoverable>
          </GlassCard>

          {!isLoadingTags && tags.length > 0 && (
            <GlassCard borderRadius={16} padding={20}>
              <Text style={[styles.sidePanelSectionTitle, { color: colors.text.dimmer }]}>Filter by Tag</Text>
              <View style={styles.sidePanelTagList}>
                <Hoverable
                  hover={filters.selectedTagIds.length === 0 ? 'none' : 'row'}
                  style={[styles.sidePanelTag, filters.selectedTagIds.length === 0 && { backgroundColor: colors.glass.buttonBg }]}
                  onPress={filters.clearTags}
                >
                  <Text style={[styles.sidePanelTagText, { color: colors.text.muted }, filters.selectedTagIds.length === 0 && styles.sidePanelTagTextActive]}>
                    All
                  </Text>
                </Hoverable>
                {tags.map((tag) => {
                  const isSelected = filters.selectedTagIds.includes(tag.id);
                  return (
                    <Hoverable
                      key={tag.id}
                      hover={isSelected ? 'none' : 'row'}
                      style={[styles.sidePanelTag, isSelected && { backgroundColor: colors.glass.buttonBg }]}
                      onPress={() => filters.toggleTag(tag.id)}
                    >
                      <View style={[styles.sidePanelTagDot, { backgroundColor: tag.color || PRIMITIVE_COLORS.resioBlue }]} />
                      <Text style={[styles.sidePanelTagText, { color: colors.text.muted }, isSelected && styles.sidePanelTagTextActive]}>
                        {tag.name}
                      </Text>
                    </Hoverable>
                  );
                })}
              </View>
            </GlassCard>
          )}
        </View>
      </View>
      {threadActionModals}
    </View>
  );
};

const ItemSeparator = () => <View style={styles.separator} />;

const styles = StyleSheet.create({
  feedContainer: {
    flex: 1,
  },
  desktopContainer: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  desktopContent: {
    flex: 1,
    flexDirection: 'row',
    // Match the marketplace desktop content frame (max width + 24 padding).
    maxWidth: 1200,
    alignSelf: 'center' as const,
    width: '100%',
    gap: 32,
    paddingTop: HP,
    paddingHorizontal: HP,
  },
  desktopFeedColumn: {
    flex: 1,
    minWidth: 0,
  },
  desktopSidePanel: {
    width: 300,
    gap: 16,
    position: 'sticky' as any,
    top: 24,
    alignSelf: 'flex-start' as const,
  },
  sidePanelTitle: {
    ...REDESIGN_TYPOGRAPHY.sectionTitle,
  },
  sidePanelProperty: {
    ...REDESIGN_TYPOGRAPHY.body,
    marginTop: 4,
  },
  sidePanelStats: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 4,
    marginTop: 12,
  },
  sidePanelStatValue: {
    ...REDESIGN_TYPOGRAPHY.cardTitleLarge,
  },
  sidePanelStatLabel: {
    ...REDESIGN_TYPOGRAPHY.caption,
  },
  sidePanelButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: PRIMITIVE_COLORS.resioBlue,
    borderRadius: 12,
    paddingVertical: 12,
  },
  sidePanelButtonText: {
    ...REDESIGN_TYPOGRAPHY.button,
    color: '#FFFFFF',
  },
  sidePanelSectionTitle: {
    ...REDESIGN_TYPOGRAPHY.label,
    marginBottom: 12,
  },
  sidePanelTagList: {
    gap: 6,
  },
  sidePanelTag: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
  },
  sidePanelTagActive: {},
  sidePanelTagDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  sidePanelTagText: {
    ...REDESIGN_TYPOGRAPHY.small,
  },
  sidePanelTagTextActive: {
    color: PRIMITIVE_COLORS.resioBlueLight,
  },
  sidePanelHeader: {
    padding: 20,
  },
  sidePanelDivider: {
    height: 1,
  },
  sidePanelActions: {
    padding: 20,
    paddingTop: 16,
  },
  sideLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 14,
    paddingHorizontal: 20,
  },
  sideLinkDivider: {
    height: 1,
    marginHorizontal: 20,
  },
  sideLinkText: {
    ...REDESIGN_TYPOGRAPHY.small,
    flex: 1,
  },
  feedContentContainer: {
    paddingHorizontal: HP,
  },
  feedContentContainerDesktop: {
    paddingHorizontal: 0,
  },
  headerSection: {
    gap: 12,
    paddingBottom: 8,
  },
  backLink: {
    marginBottom: -4,
  },
  announcementsSection: {
    gap: 12,
    paddingTop: 4,
  },
  separator: {
    height: 16,
  },
  footerLoader: {
    paddingVertical: 20,
    alignItems: 'center',
  },
  fab: {
    position: 'absolute',
    right: 20,
    width: 52,
    height: 52,
    borderRadius: 16,
    backgroundColor: PRIMITIVE_COLORS.resioBlue,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: PRIMITIVE_COLORS.resioBlue,
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.4,
    shadowRadius: 24,
    elevation: 8,
  },
});
